import React, { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableToken, useCollectionBinding, useProperty } from '@vibesites/runtime/react';
import basePagesData from '@vibesites/theme-via6/pages.json';
import childPagesData from '../pages.json';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

// ============================================================================
// Pages data: shallow-merge parent (Via6 base) + child (Weidner overrides)
// ----------------------------------------------------------------------------
// The base via6 `SitemapContent` reads pages.json via a *static*
// `import pagesData from '../pages.json'`, which resolves to the parent theme's
// own pages.json and therefore never picks up child-theme overrides. This fork
// merges the Weidner child pages.json on top of the base (child fields win,
// parent fields fill in) — the same shallow-per-alias semantics the forked
// `_HeaderBase.tsx` uses — so the sitemap list reflects the full merged page
// hierarchy rather than only Weidner's partial override set.
// ============================================================================
const pagesData: any = (() => {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [alias, data] of Object.entries(basePagesData as Record<string, Record<string, unknown>>)) {
    out[alias] = { ...data };
  }
  for (const [alias, data] of Object.entries(childPagesData as Record<string, Record<string, unknown>>)) {
    out[alias] = { ...out[alias], ...data };
  }
  return out;
})();

/**
 * Weidner override of the base via6 `SitemapContent`.
 *
 * Identical to the base component except the "Interactive Map"
 * (`/interactive-map`) page link is gated on the property having an Engrain
 * SightMap URL (`custom_engrain_sightmap_url`) — same condition the header
 * nav uses — so the on-page sitemap and the nav surface the page together or
 * not at all.
 */
export function SitemapContent() {
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map gate — mirrors the header nav (`_HeaderBase.tsx`): the
  // page is surfaced only when the property has an Engrain SightMap URL to
  // drive the interactive site-map widget. `custom_engrain_sightmap_url` is a
  // custom property field (typed `text`), so we read it off `useProperty()`
  // directly rather than the strongly-typed `usePropertyField`. Keeping this
  // in lock-step with the nav means a property either shows Interactive Map in
  // BOTH the nav and the on-page sitemap, or in neither — no inconsistency.
  const property = useProperty() as Record<string, unknown>;
  const engrainSightmapUrl = property?.custom_engrain_sightmap_url as
    | string
    | null
    | undefined;
  const hasInteractiveLeasing = !!(engrainSightmapUrl && String(engrainSightmapUrl).trim());

  const pages = useMemo(
    () =>
      Object.values(pagesData as Record<string, PageEntry>)
        .filter((p) => {
          if (p.path === '/sitemap' || p.path.includes('{{')) return false;
          // Show Interactive Map only when the property has a SightMap URL
          // (was hidden unconditionally before).
          if (p.path === '/interactive-map') return hasInteractiveLeasing;
          if (p.path === '/virtual-tour' && !hasVirtualTours) return false;
          return true;
        })
        .sort((a, b) => a.position - b.position),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="sitemapcontent.heading"
          as="h1"
          className="font-heading text-3xl font-normal text-[var(--vibe-color-primary)] mb-10 text-center"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              className="block border border-[var(--vibe-color-primary)] rounded px-6 py-4 text-center font-body text-sm font-medium text-[var(--vibe-color-primary)] bg-[var(--vibe-color-background)] hover:bg-[var(--vibe-color-primary)] hover:text-[var(--vibe-color-text-on-primary)] hover:border-[var(--vibe-color-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vibe-color-secondary)] transition-colors"
            >
              {page.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {
    'sitemapcontent.heading': {
      label: 'Page Heading',
      type: 'text',
      defaultValue: 'Sitemap',
    },
  },
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
